import { useQuery } from '@apollo/client'
import { useUserData } from '@nhost/react'
import { MessageCircle } from 'lucide-react'

import { cn } from '@/shared/lib/utils'

import { GET_CHAT_CONNECTION } from './api'
import { useUnreadChatMessages } from './useUnreadChatMessages'
import { useUnreadFeedbackMessages } from './useUnreadFeedbackMessages'

type ChatNavIconProps = {
  viewer: 'woman' | 'midwife' | 'institute'
  className?: string
}

/**
 * Das Chat-Symbol der Navigation mit der Zahl ungelesener Nachrichten.
 *
 * - Frau und Hebamme zählen ihre Chats und das Gespräch mit dem Institut zusammen, beides
 *   steht in ihrer Chatliste.
 * - Die Frau zählt dabei nur das Gespräch mit der verbundenen Hebamme: Nach einem Wechsel
 *   blieben Nachrichten der früheren sonst für immer ungelesen in der Zahl stehen, weil
 *   der Chat sie nicht mehr zeigt.
 * - Das Institut zählt nur Feedback.
 */
export function ChatNavIcon({ viewer, className }: ChatNavIconProps) {
  const user = useUserData()
  const isWoman = viewer === 'woman'
  const isInstitute = viewer === 'institute'
  const unreadChat = useUnreadChatMessages(!isInstitute)
  const unreadFeedback = useUnreadFeedbackMessages(isInstitute ? 'institute' : 'member')
  const { data } = useQuery(GET_CHAT_CONNECTION, {
    variables: { userId: user?.id ?? '' },
    skip: !isWoman || !user?.id,
  })
  const connectedMidwifeId = data?.user_profiles?.[0]?.connected_midwife_id

  const chatCount = isWoman
    ? unreadChat.filter((message) => message.midwife_user_id === connectedMidwifeId).length
    : unreadChat.length
  const count = chatCount + unreadFeedback.length

  return (
    <span className="relative">
      <MessageCircle className={className} />
      {count > 0 && (
        <span
          className={cn(
            'absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1',
            'bg-destructive text-[10px] font-medium leading-none text-destructive-foreground'
          )}
          aria-label={`${count} ungelesen`}
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
    </span>
  )
}
